"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePublishOnboardingContext = void 0;
const ui_context_1 = require("@app/reducer/ui-context");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const react_1 = __importDefault(require("react"));
/**
 * Publishes the checklist the manager is looking at into the store, so the manager
 * assistant can see it (`app/reducer/ui-context.ts` → the chat's `metadata.viewContext`).
 * The assistant is meant to help finish these steps, and it cannot read them from its own
 * API: which steps exist at all, and the order they are in, is decided here in the frontend.
 *
 * Nothing is published while the query is still loading — a half-empty checklist would read
 * as „almost nothing is set up".
 *
 * The snapshot is dropped whenever there is nothing to publish, not only on unmount: the
 * caller is `OnboardingProvider`, which lives above the router and therefore never unmounts,
 * and it switches the query off once the manager is neither on the checklist nor working on a
 * step. Without the clear, the last snapshot would sit in the store going stale while nothing
 * updated it, and the assistant would read it as the current screen.
 */
const usePublishOnboardingContext = ({ isLoading, steps, completedCount, totalCount, activeIndex, }) => {
    const dispatch = (0, store_1.useRootDispatch)();
    const snapshot = isLoading
        ? null
        : {
            completedCount,
            totalCount,
            activeStepId: activeIndex >= 0 ? steps[activeIndex].id : null,
            steps: steps.map((step) => ({
                id: step.id,
                done: step.isDone,
                ...((0, util_1.isValue)(step.autoCount) ? { count: step.autoCount } : {}),
            })),
        };
    // `steps` is rebuilt on every render, so the publish is keyed on the snapshot's VALUE —
    // keying it on the object itself would re-dispatch on every render of the page.
    const serialized = (0, util_1.isValue)(snapshot) ? JSON.stringify(snapshot) : null;
    react_1.default.useEffect(() => {
        if ((0, util_1.isValue)(serialized)) {
            dispatch((0, ui_context_1.publishedOnboardingContext)(JSON.parse(serialized)));
        }
        else {
            // Also the very first run, before the first answer has landed. Immer treats clearing an
            // already-empty entry as no change at all, so that costs nothing.
            dispatch((0, ui_context_1.clearedOnboardingContext)());
        }
    }, [dispatch, serialized]);
};
exports.usePublishOnboardingContext = usePublishOnboardingContext;
exports.default = exports.usePublishOnboardingContext;
